import React, { useEffect, useState, useRef, useCallback } from 'react';
import { mfaApiCall } from '../services/api';

interface MFAStatus {
  mfaRequired: boolean;
  mfaSetupComplete: boolean;
  mfaSetupDate: string | null;
  mfaLastUsed: string | null;
  mfaRequiredBy: string | null;
  mfaRequiredDate: string | null;
}

const MFANotificationBell: React.FC = () => {
  const [mfaStatus, setMfaStatus] = useState<MFAStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const fetchMFAStatus = useCallback(async () => {
    try {
      const status = await mfaApiCall('/auth/mfa/status');
      setMfaStatus(status);
    } catch (error: any) {
      console.error('Error fetching MFA status:', error?.message);
      setMfaStatus({
        mfaRequired: false,
        mfaSetupComplete: false,
        mfaSetupDate: null,
        mfaLastUsed: null,
        mfaRequiredBy: null,
        mfaRequiredDate: null
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMFAStatus();
  }, [fetchMFAStatus]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (loading || !mfaStatus) {
    return null;
  }

  // Don't show notification if MFA is not required
  if (!mfaStatus.mfaRequired) {
    return null;
  }

  const hasAlert = mfaStatus.mfaRequired && !mfaStatus.mfaSetupComplete;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Notification Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-label="MFA Notifications"
      >
        {/* Bell Icon */}
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>

        {/* Badge Indicator */}
        {hasAlert ? (
          <span className="absolute top-1 right-1 flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
          </span>
        ) : (
          <span className="absolute top-1 right-1 inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
        )}
      </button>

      {/* Dropdown Popover */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-1rem)] bg-white rounded-lg shadow-xl border border-gray-200 z-50">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">
                Two-Factor Authentication
              </h3>
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium ${
                  hasAlert
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-green-100 text-green-800'
                }`}
              >
                {hasAlert ? 'Pending Setup' : 'Active'}
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="px-4 py-3 space-y-3">
            {hasAlert && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <div className="flex">
                  <svg
                    className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-yellow-800">
                      Action Required
                    </p>
                    <p className="text-xs text-yellow-700 mt-1">
                      You'll be prompted to set up MFA on your next login. Have your authenticator app ready.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Details */}
            <div className="space-y-2 text-sm">
              {mfaStatus.mfaRequiredDate && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Required on:</span>
                  <span className="text-gray-900 font-medium">
                    {new Date(mfaStatus.mfaRequiredDate).toLocaleDateString()}
                  </span>
                </div>
              )}

              {mfaStatus.mfaSetupDate && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Setup completed:</span>
                  <span className="text-gray-900 font-medium">
                    {new Date(mfaStatus.mfaSetupDate).toLocaleDateString()}
                  </span>
                </div>
              )}

              {mfaStatus.mfaLastUsed && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Last used:</span>
                  <span className="text-gray-900 font-medium">
                    {new Date(mfaStatus.mfaLastUsed).toLocaleString()}
                  </span>
                </div>
              )}

              {mfaStatus.mfaRequiredBy && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Required by:</span>
                  <span className="text-gray-900 font-medium">
                    {mfaStatus.mfaRequiredBy}
                  </span>
                </div>
              )}
            </div>

            {/* Info Footer */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-2 mt-3">
              <div className="flex">
                <svg
                  className="h-4 w-4 text-blue-400 mr-2 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-xs text-blue-700">
                  MFA is managed by your administrator. Contact them for assistance.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MFANotificationBell;
